import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ResetPasswordDto } from '../../interfaces/resetPasswordDto.model';
import { PasswordConfirmationValidatorService } from 'src/app/core/custom-validators/password-confirmation-validator.service';
import { AuthService } from '../../services/auth/auth.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent, XdsInputComponent, XdsFormFieldComponent } from '../../../../shared/ui';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.css'],
    imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent, XdsInputComponent, XdsFormFieldComponent]
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  resetPasswordForm: UntypedFormGroup;
  showSuccess: boolean;
  showError: boolean;
  errorMessage: string;
  showPassword = false;
  showConfirmPassword = false;
  passwordStrength = 0; // NEU: Für die Passwort-Stärke-Anzeige

  private token: string;
  private email: string;

  constructor(
    private service: AuthService,
    private passConfValidator: PasswordConfirmationValidatorService,
    private route: ActivatedRoute,
    public themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.resetPasswordForm = new UntypedFormGroup({
      password: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(12),
        this.validatePasswordStrength.bind(this),
      ]),
      confirm: new UntypedFormControl(''),
    });

    this.resetPasswordForm
      .get('confirm')
      .setValidators([
        Validators.required,
        this.passConfValidator.validateConfirmPassword(
          this.resetPasswordForm.get('password')
        ),
      ]);

    // NEU: Subscribe to password changes for strength indicator
    this.resetPasswordForm.get('password')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.passwordStrength = this.calculatePasswordStrength(value);
    });

    // Token und Email aus Query-Parametern extrahieren
    this.token = this.route.snapshot.queryParams['token'];
    this.email = this.route.snapshot.queryParams['email'];

    // Fallback: Wenn queryParams leer sind, versuche manuelle Extraktion aus URL
    if (!this.token || !this.email) {
      const urlParams = new URLSearchParams(window.location.search);
      this.token = this.token || urlParams.get('token');
      this.email = this.email || urlParams.get('email');
    }

    // Wenn immer noch keine Parameter vorhanden sind, zeige Fehler
    if (!this.token || !this.email) {
      this.showError = true;
      // i18n-Schlüssel, nicht Klartext: das Banner übersetzt jetzt (siehe Template).
      this.errorMessage = 'RESET_PASSWORD_INVALID_LINK';
      console.error('Reset Password Component - Token oder Email fehlen!');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public validateControl = (controlName: string) => {
    return (
      this.resetPasswordForm.get(controlName).invalid &&
      this.resetPasswordForm.get(controlName).touched
    );
  };

  public hasError = (controlName: string, errorName: string) => {
    return this.resetPasswordForm.get(controlName).hasError(errorName);
  };

  public resetPassword = (resetPasswordFormValue: { password: string; confirm: string }) => {
    this.showError = this.showSuccess = false;
    const resetPass = { ...resetPasswordFormValue };

    const resetPassDto: ResetPasswordDto = {
      password: resetPass.password,
      confirmPassword: resetPass.confirm,
      token: this.token,
      email: this.email,
    };

    // Der Server meldet jeden Fehlschlag als `200 false` (Enumerations-Schutz). Ohne diesen
    // Rückkanal blieben `showError`/`errorMessage` für immer leer — sie wurden bisher nur beim
    // fehlenden Token gesetzt, nie aus einer Server-Antwort. Ein Toast allein ist nach dem
    // Wegklicken verloren; die Meldung soll im Formular stehen bleiben.
    this.service.resetPassword(resetPassDto, (messageKey) => {
      this.showError = true;
      this.errorMessage = messageKey;
    });
  };

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // NEU: Passwort-Stärke berechnen (0-5)
  calculatePasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;

    // Länge >= 12
    if (password.length >= 12) strength++;

    // Enthält Großbuchstaben
    if (/[A-Z]/.test(password)) strength++;

    // Enthält Kleinbuchstaben
    if (/[a-z]/.test(password)) strength++;

    // Enthält Ziffern
    if (/[0-9]/.test(password)) strength++;

    // Enthält Sonderzeichen
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    return strength;
  }

  // Passwort-Stärke-Label (returns translation key)
  getPasswordStrengthLabel(): string {
    switch (this.passwordStrength) {
      case 0:
      case 1: return 'REG_PW_LEVEL_WEAK';
      case 2: return 'REG_PW_LEVEL_MEDIUM';
      case 3: return 'REG_PW_LEVEL_GOOD';
      case 4: return 'REG_PW_LEVEL_STRONG';
      case 5: return 'REG_PW_LEVEL_VERY_STRONG';
      default: return 'REG_PW_LEVEL_WEAK';
    }
  }

  getPasswordStrengthData(): 'weak' | 'full' | null {
    if (this.passwordStrength <= 1) return 'weak';
    if (this.passwordStrength === 5) return 'full';
    return null;
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }

  validatePasswordStrength(
    control: UntypedFormControl
  ): { [key: string]: boolean } | null {
    const value = control.value || '';
    const errors: { [key: string]: boolean } = {};

    if (value.length < 12) {
      errors['minlength'] = true;
    }
    if (!/[A-Z]/.test(value)) {
      errors['missingUpper'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['missingLower'] = true;
    }
    if (!/[0-9]/.test(value)) {
      errors['missingDigit'] = true;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['missingSpecial'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }
}
